/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";

import { useLocale } from "@superweb/intl";
import {
  Button,
  ComboBox,
  createComboBoxState,
  createTextFieldState,
  ExperimentalFormRow,
  Snackbar,
  TextField,
  useDebouncedState,
  type ComboBoxOption,
  type ComboBoxState,
  type TextFieldState,
} from "@superweb/ui";
import { useFlag } from "@superweb/flags";

import { useAction, useOnDemandQuery, useQuery } from "@fleet/api";

import { useLogAction } from "#internal/analytics/logActions";
import {
  searchParamTypes as t,
  useMakeLink,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import {
  AppLayout,
  Form,
  NotFound,
  Offer,
  OfferDeprecated,
} from "#internal/ui";
import { useMessage } from "#internal/intl";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useValidate,
} from "#internal/utils";
import { useSubmitErrorMessages } from "#internal/utils/validation";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import { getSteps } from "../steps";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

type VehicleFormState = {
  number: TextFieldState;
  brand: ComboBoxState<ComboBoxOption>;
  model: ComboBoxState<ComboBoxOption>;
  color_code: ComboBoxState<ComboBoxOption>;
  year: ComboBoxState<ComboBoxOption>;
};

const Content = ({
  refId,
  sessionId,
  specification,
  professionId,
  employmentType,
  leadId,
  proId,
  previousStep,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  isIframe,
}: {
  refId: string;
  sessionId: string;
  specification?: string;
  professionId?: string;
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  leadId?: string;
  proId?: boolean;
  previousStep?: "profession" | "employment-type";
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  isIframe: boolean;
}) => {
  const locale = useLocale();
  const message = useMessage();
  const navigate = useNavigate();
  const logActionContact = useLogAction("contact");
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const flag = useFlag();
  const makeLink = useMakeLink();

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId,
      profession_id: professionId,
      employment_type: employmentType,
    },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const errorMessages = useErrorMessages();
  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );
  const { validateTextField, validateComboboxField } = useValidate();
  const [errorMessage, setErrorMessage] = useState<string>();

  // TODO: change to dynamic if it is actual
  const [vehicleType] = useState<"car" | "bike" | "rickshaw">("car");

  const [vehicleState, setVehicleState] = useState<VehicleFormState>(() => ({
    number: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
    brand: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    model: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    color_code: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    year: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const debouncedBrandInputValue = useDebouncedState(
    vehicleState.brand.inputValue,
    300,
  );
  const debouncedModelInputValue = useDebouncedState(
    vehicleState.model.inputValue,
    300,
  );
  const debouncedColorCodeInputValue = useDebouncedState(
    vehicleState.color_code.inputValue,
    300,
  );
  const debouncedYearInputValue = useDebouncedState(
    vehicleState.year.inputValue,
    300,
  );

  const { brandsOptions, modelsOptions, colorCodesOptions, yearsOptions } =
    useOptions({
      refId,
      vehicleType,
      brandValue: vehicleState.brand.value?.key,
      brandInputValue: debouncedBrandInputValue,
      modelInputValue: debouncedModelInputValue,
      colorCodeInputValue: debouncedColorCodeInputValue,
      yearInputValue: debouncedYearInputValue,
    });

  const onChangeVehiclePartial = useOnChangePartial(setVehicleState);
  const onShowErrors = useOnShowErrors(setVehicleState);

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const professionSettings = parkInfo?.profession_settings_list.find(
    (profession) => profession.profession_id === professionId,
  );

  const { stepsCount, currentStep, nextStep } = getSteps({
    formRequirements:
      professionSettings?.form_requirements ?? parkInfo?.form_requirements,
    currentStep: "vehicle",
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    isLoginWithPro: proId,
    employmentType,
  });

  const onSubmit = () => {
    onShowErrors();

    const hasErrors = Object.keys(vehicleState).some(
      (field) =>
        vehicleState[field as keyof VehicleFormState].errorMessage !==
        undefined,
    );

    if (hasErrors) {
      if (vehicleState.number.errorMessage === errorMessages.invalidCarNumber) {
        logActionInvalid("invalid_car_number", { onlyYM: true });
      }

      return;
    }

    actionSubmit(
      {
        headers: {
          "x-submission-id": sessionId,
          language: locale.toString(),
          "content-type": "application/json",
        },
        query: { ref_id: refId, apply_auth: proId },
        body: {
          car: {
            number: vehicleState.number.value,
            brand: String(vehicleState.brand.value?.key),
            model: String(vehicleState.model.value?.key),
            color_code: String(vehicleState.color_code.value?.key),
            year: Number(vehicleState.year.value?.key),
          },
        },
      },
      {
        onResponse: (res) => {
          if (res.status === 204) {
            logActionSubmit("submit_vehicle");
            navigate(`/${nextStep}?${searchParams}`);
            return;
          }

          switch (res.body.code) {
            case "invalid_car_number": {
              logActionInvalid("invalid_car_number", { onlyYM: true });
              onChangeVehiclePartial({
                number: {
                  ...vehicleState.number,
                  errorMessage: errorMessages.invalidCarNumber,
                  errorVisible: true,
                },
              });
              break;
            }
            default: {
              setErrorMessage(
                submitErrorMessages[res.body.code] ?? errorMessages.default,
              );
              break;
            }
          }
        },
      },
    );
  };

  const title = message({
    id: "019a9d51-ad3e-74a8-a7f3-f37e9d90e97e",
    context: "Vehicle details form. Header title",
    default: "Vehicle details",
  });

  const searchParams = new URLSearchParams({
    ref_id: refId,
    ...(sessionId ? { sessionId } : undefined),
    ...(specification ? { specification } : undefined),
    ...(professionId ? { profession_id: professionId } : undefined),
    ...(employmentType ? { employment_type_id: employmentType } : undefined),
    ...(leadId ? { lead_id: leadId } : undefined),
    ...(proId ? { pro_id: proId.toString() } : undefined),
  });

  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo?.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "ccc86c7d-c0d6-401a-89d1-81ded0bffc2d",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo?.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <>
      <AppLayout
        title={isIframe ? undefined : title}
        partnerName={parkInfo?.partner_name}
        content={
          <Form
            header={{
              title,
              ...(currentStep && {
                step: {
                  current: currentStep,
                  total: stepsCount,
                },
              }),
              ...(previousStep
                ? {
                    previousStep: makeLink(`/${previousStep}?${searchParams}`),
                  }
                : {}),
            }}
            content={
              <>
                <ExperimentalFormRow columnsCount={1}>
                  <TextField
                    required
                    state={vehicleState.number}
                    onChange={(state) => {
                      const errorMessage = validateTextField(state.value);
                      onChangeVehiclePartial({
                        number: {
                          value: state.value.toUpperCase(),
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                    label={message({
                      id: "019a9d95-d10a-752f-bad0-df37a8894f8e",
                      context: "Vehicle details form. Vehicle number field",
                      default: "License plate number",
                    })}
                  />
                </ExperimentalFormRow>
                <ExperimentalFormRow columnsCount={2}>
                  <ComboBox
                    required
                    label={message({
                      id: "019a9d5e-aa43-763f-9858-31391ccbe70f",
                      context: "Vehicle details form. Brand field label",
                      default: "Make",
                    })}
                    state={vehicleState.brand}
                    options={brandsOptions}
                    onChange={(state) => {
                      const errorMessage = validateComboboxField(state);
                      onChangeVehiclePartial({
                        brand: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                        model: {
                          value:
                            vehicleType === "car"
                              ? undefined
                              : modelsOptions[0],
                          inputValue: "",
                          errorMessage:
                            vehicleType === "car"
                              ? errorMessages.requiredField
                              : undefined,
                          errorVisible: vehicleType === "car",
                        },
                      });
                    }}
                  />
                  <ComboBox
                    required
                    label={message({
                      id: "019a9d5e-ef66-755d-9fb4-9c0340d1aee6",
                      context: "Vehicle details form. Model field label",
                      default: "Model",
                    })}
                    disabled={!vehicleState.brand.value?.key}
                    state={vehicleState.model}
                    options={modelsOptions}
                    onChange={(state) => {
                      const errorMessage = validateComboboxField(state);
                      onChangeVehiclePartial({
                        model: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                  />
                </ExperimentalFormRow>
                <ExperimentalFormRow columnsCount={2}>
                  <ComboBox
                    required
                    label={message({
                      id: "019a9d5f-40e5-75fb-9123-ddb9970a83a8",
                      context: "Vehicle details form. Color field label",
                      default: "Color",
                    })}
                    state={vehicleState.color_code}
                    options={colorCodesOptions}
                    onChange={(state) => {
                      const errorMessage = validateComboboxField(state);
                      onChangeVehiclePartial({
                        color_code: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                  />
                  <ComboBox
                    required
                    label={message({
                      id: "019a9d5f-78bd-76de-8f97-7c4f11cfcf9c",
                      context: "Vehicle details form. Year field label",
                      default: "Year",
                    })}
                    state={vehicleState.year}
                    options={yearsOptions}
                    onChange={(state) => {
                      const errorMessage = validateComboboxField(state);
                      onChangeVehiclePartial({
                        year: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                  />
                </ExperimentalFormRow>
              </>
            }
            onSubmit={onSubmit}
            footer={{
              main: (
                <Button
                  variant="action"
                  type="submit"
                  shape="squircle"
                  progress={isFetching}
                  text={message({
                    id: "019a9d5a-05e0-762f-b8f4-263a79bdb46e",
                    context: "Vehicle details form. Submit button text",
                    default: "Save",
                  })}
                  size="l"
                />
              ),
            }}
          />
        }
        offer={
          isIframe ? undefined : enableNewOffer ? (
            <Offer
              image={
                enableSerbiaOfferImage
                  ? {
                      avif: SerbiaOfferAvif,
                      png: SerbiaOfferPng,
                      webp: SerbiaOfferWebp,
                      height: "130px",
                      width: "178px",
                      padding: "24px",
                    }
                  : undefined
              }
              offerText={parkInfo?.promo_text}
              type={offerType}
              onPhonePress={() =>
                logActionContact("phone_click_v2", {
                  params: { stage: "register" },
                })
              }
              onMessengerPress={() =>
                logActionContact("messenger_click_v2", {
                  params: { stage: "register" },
                })
              }
              phone={parkInfo?.phone}
              messageLink={parkInfo?.message_link}
            />
          ) : (
            <OfferDeprecated
              parkId={parkInfo?.park_id}
              offerText={parkInfo?.promo_text}
              isDelivery={specification === "delivery"}
              onPhonePress={() =>
                logActionContact("phone_click_v2", {
                  params: { stage: "register" },
                })
              }
              onMessengerPress={() =>
                logActionContact("messenger_click_v2", {
                  params: { stage: "register" },
                })
              }
              phone={parkInfo?.phone}
              messageLink={parkInfo?.message_link}
            />
          )
        }
      />
      {errorMessage && (
        <Snackbar
          text={errorMessage}
          view="critical"
          onClose={() => {
            setErrorMessage(undefined);
          }}
        />
      )}
    </>
  );
};

export const VehicleForm = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id: refId,
      sessionId,
      specification,
      profession_id: professionId,
      employment_type_id: employmentType,
      lead_id: leadId,
      pro_id: proId,
      previous_step: previousStep,
      is_multiple_professions: isMultipleProfessions,
      is_multiple_employment_types: isMultipleEmploymentTypes,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    specification: t.string,
    profession_id: t.string,
    employment_type_id: t.enumString([
      "selfemployed",
      "individual_entrepreneur",
      "park_employee",
    ] as const),
    lead_id: t.string,
    pro_id: t.boolean,
    previous_step: t.enumString(["profession", "employment-type"] as const),
    is_multiple_professions: t.boolean,
    is_multiple_employment_types: t.boolean,
  });

  if (!refId || !sessionId) {
    return <NotFound />;
  }

  return (
    <Content
      refId={refId}
      sessionId={sessionId}
      specification={specification}
      professionId={professionId}
      employmentType={employmentType}
      leadId={leadId}
      proId={proId}
      previousStep={previousStep}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      isIframe={isIframe}
    />
  );
};

const useOptions = ({
  refId,
  vehicleType,
  brandValue,
  brandInputValue,
  modelInputValue,
  colorCodeInputValue,
  yearInputValue,
}: {
  refId: string;
  vehicleType: "car" | "bike" | "rickshaw";
  brandValue?: string;
  brandInputValue?: string;
  modelInputValue?: string;
  colorCodeInputValue?: string;
  yearInputValue?: string;
}) => {
  const locale = useLocale();

  const { response: brandResponse } = useOnDemandQuery({
    url: "/fleet-forms/selfreg/v1/car/brands",
    method: "GET",
    headers: {
      language: locale.toString(),
    },
    query: {
      ref_id: refId,
      text_query: brandInputValue ?? "",
      vehicle_type: vehicleType,
    },
  });
  const brandsOptions =
    brandResponse?.status === 200
      ? brandResponse.body.brands.map((item) => ({ label: item, key: item }))
      : [];

  const { response: modelResponse } = useOnDemandQuery(
    brandValue
      ? {
          url: "/fleet-forms/selfreg/v1/car/models",
          method: "GET",
          headers: {
            language: locale.toString(),
          },
          query: {
            ref_id: refId,
            brand: brandValue,
            text_query: modelInputValue ?? "",
            vehicle_type: vehicleType,
          },
        }
      : undefined,
  );
  const modelsOptions =
    modelResponse?.status === 200
      ? modelResponse.body.models.map((item) => ({ label: item, key: item }))
      : [];

  const { response: colorCodeResponse } = useOnDemandQuery({
    url: "/fleet-forms/selfreg/v1/car/colors",
    method: "GET",
    headers: {
      language: locale.toString(),
    },
  });
  const colorCodesOptions =
    colorCodeResponse?.status === 200
      ? colorCodeResponse.body.colors.flatMap(({ name, hex_code }) => {
          const isMatched = name
            .toLowerCase()
            .includes(colorCodeInputValue?.toLowerCase() ?? "");

          return isMatched
            ? {
                label: name,
                key: hex_code,
              }
            : [];
        })
      : [];

  const { response: yearResponse } = useOnDemandQuery({
    url: "/fleet-forms/selfreg/v1/car/years",
    method: "GET",
    headers: {
      language: locale.toString(),
    },
  });

  const yearsOptions =
    yearResponse?.status === 200
      ? yearResponse.body.years.flatMap((item) => {
          const isMatched = String(item).includes(
            yearInputValue?.toLowerCase() ?? "",
          );

          return isMatched
            ? {
                label: String(item),
                key: String(item),
              }
            : [];
        })
      : [];

  return {
    brandsOptions,
    modelsOptions,
    colorCodesOptions,
    yearsOptions,
  };
};
